import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a session ID if not exists
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
  };
};

// Get device info
const getDeviceInfo = () => {
  return {
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const useAnalytics = () => {
  const location = useLocation();
  const sessionStarted = useRef(false);
  const lastPage = useRef(null);

  // Track page view
  const trackPageView = useCallback(async (page) => {
    if (page === lastPage.current) return; // Avoid duplicate tracking
    lastPage.current = page;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/analytics/pageview`, {
        page,
        referrer: document.referrer,
        session_id: getSessionId(),
        ...getUTMParams(),
        ...getDeviceInfo(),
      }, { headers });
    } catch (error) {
      // Silent fail - don't break the app for analytics
      console.debug('Analytics pageview error:', error.message);
    }
  }, []);

  // Track session start
  const trackSessionStart = useCallback(async () => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/analytics/session`, {
        session_id: getSessionId(),
        referrer: document.referrer,
        landing_page: window.location.pathname,
        ...getUTMParams(),
        ...getDeviceInfo(),
      }, { headers });
    } catch (error) {
      console.debug('Analytics session error:', error.message);
    }
  }, []);

  // Track feature usage
  const trackFeature = useCallback(async (feature, metadata = {}) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/analytics/feature`, {
        feature,
        metadata,
        session_id: getSessionId(),
      }, { headers });
    } catch (error) {
      console.debug('Analytics feature error:', error.message);
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackSessionStart();
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView, trackSessionStart]);

  return { trackPageView, trackFeature };
};

export default useAnalytics;
